import React, { useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';

import { FormControl, FormLabel, FormHelperText, TextField, Typography } from '@mui/material';

import styles from './styles';
import { PathwayStep, FREETEXT_DEFAULT_CHARACTER_LIMIT } from '../../../Common';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  value: string;
  data: PathwayStep;
  validate: () => void;
  handleChange: (i: string) => void;
}

const PathwayStepFreetext = ({ classes, value, data, validate, handleChange }: Props): React.ReactElement => {
  const [touched, setTouched] = useState(false);
  const characterLimit = data.character_limit ?? FREETEXT_DEFAULT_CHARACTER_LIMIT;
  const currentLength = value?.length || 0;
  const isOverLimit = currentLength > characterLimit;
  const isEmpty = data.mandatory && !value?.trim();
  const hasError = touched && (isOverLimit || isEmpty);

  const getHelperText = () => {
    if (touched && isEmpty) {
      return 'This field is required';
    }
    if (isOverLimit) {
      return `Character limit exceeded (${currentLength}/${characterLimit})`;
    }
    return data.description;
  };

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl} required={data.mandatory} component="fieldset" error={hasError}>
        <FormLabel className={classes.name} component="legend">
          {data.name}
        </FormLabel>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          variant="outlined"
          placeholder="Enter text here..."
          value={value}
          error={hasError}
          inputProps={{
            'aria-label': data.name,
            'aria-required': data.mandatory,
            'aria-describedby': `${data.id}-helper-text`,
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(event.target.value);
            if (!touched) setTouched(true);
          }}
          onBlur={() => {
            setTouched(true);
            validate();
          }}
        />
        <div className={classes.characterCount}>
          <FormHelperText id={`${data.id}-helper-text`} className={classes.description}>
            {getHelperText()}
          </FormHelperText>
          <Typography variant="caption" className={isOverLimit ? classes.characterCountError : undefined}>
            {currentLength}/{characterLimit}
          </Typography>
        </div>
      </FormControl>
    </div>
  );
};

export default withStyles(styles)(PathwayStepFreetext);
